import React, { useState } from "react";

import isIntuit from "Intuit/isIntuit";

import { Collapse } from "Cobalt/Collapse";
import LoadingBar from "Cobalt/LoadingBar";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import { SizeSwitch } from "Common/Responsive";
import { Body1, Body2, Caption } from "Theme/BodyText";
import Button from "Theme/Button";
import { Card } from "Theme/Card";
import DottedBox from "Theme/DottedBox";

import makeBem from "Utils/makeBem";

const bem = makeBem("CompactAncillaryPlanCard");

export const FlexWrapper = ({ children, ...rest }) => (
  <Div {...rest} className={bem("FlexWrapper")}>
    {children}
  </Div>
);

export const PlanDetailsCollapse = ({ children, ...rest }) => {
  const [open, setOpen] = useState(false);
  return (
    <Div {...rest}>
      <Button
        className={bem("FlexWrapper")}
        color="tertiary"
        fullWidth
        px={0}
        intuitProps={{ color: "link", style: { maxWidth: "100%" } }}
        classicProps={{ color: "link" }}
        onClick={() => setOpen(!open)}
      >
        <span>Plan Details</span>
        <Icon icon={open ? "chevron-up" : "chevron-down"} />
      </Button>
      <Collapse in={open}>
        <div>
          {/* Collapse animation requires extra <div> to be smooth */}
          <div>{children}</div>
        </div>
      </Collapse>
    </Div>
  );
};

const AddCard = ({ icon, name, additionalInfo, ...rest }) => (
  <DottedBox p={4} {...rest} className={bem("desktop")}>
    <FlexWrapper>
      {icon}
      <Button p={0} color="tertiary" size="medium" intuitProps={{ color: "link" }} classicProps={{ color: "link" }}>
        {isIntuit() ? "+ " : <Icon icon="plus" mr={3} />} Add
      </Button>
    </FlexWrapper>
    <Body1 componentClass="h4" weight="bold" py={3} my={2} intuitProps={{ py: 2, my: 1 }}>
      {name}
    </Body1>
    {additionalInfo}
  </DottedBox>
);

const MobileAddCard = ({ icon, name, additionalInfo, ...rest }) => (
  <DottedBox p={4} {...rest}>
    <FlexWrapper>
      {icon}
      <Div ml={4} mr="auto">
        <Body1 componentClass="h4" weight="bold" my={0}>
          {name}
        </Body1>
        {additionalInfo}
      </Div>
      <Button p={0} color="tertiary" size="medium" intuitProps={{ color: "link" }} classicProps={{ color: "link" }}>
        {isIntuit() ? "+ " : <Icon icon="plus" mr={3} />} Add
      </Button>
    </FlexWrapper>
  </DottedBox>
);

export const ResponsiveAddCard = (props) => <SizeSwitch xs={<MobileAddCard {...props} />} md={<AddCard {...props} />} />;

const DisabledAddCard = ({ icon, name, additionalInfo, ...rest }) => (
  <DottedBox p={4} {...rest} disabled>
    <FlexWrapper>
      {icon}
      <Button
        p={0}
        color="info"
        size="medium"
        intuitProps={{
          color: "link",
          className: bem("DisabledAddButton", "intuit"),
        }}
        classicProps={{ color: "link-black" }}
        disabled
      >
        {isIntuit() ? "+ " : <Icon icon="plus" mr={3} />} Add
      </Button>
    </FlexWrapper>
    <Body1 componentClass="h4" weight="bold" py={3} my={2} intuitProps={{ py: 2, my: 1 }} color="muted">
      {name}
    </Body1>
    {additionalInfo}
  </DottedBox>
);

const MobileDisabledAddCard = ({ icon, name, additionalInfo, ...rest }) => (
  <DottedBox p={4} {...rest} disabled>
    <FlexWrapper>
      {icon}
      <Div ml={4} mr="auto">
        <Body1 componentClass="h4" my={0} weight="bold" color="muted">
          {name}
        </Body1>
        {additionalInfo}
      </Div>
      <Button p={0} color="info" size="medium" intuitProps={{ color: "link-black", style: { border: "none" } }} classicProps={{ color: "link-black" }} disabled>
        {isIntuit() ? "+ " : <Icon icon="plus" mr={3} />} Add
      </Button>
    </FlexWrapper>
  </DottedBox>
);

export const ResponsiveDisabledAddCard = (props) => <SizeSwitch xs={<MobileDisabledAddCard {...props} />} md={<DisabledAddCard {...props} />} />;

const LoadingCard = ({ icon, name, ...rest }) => (
  <Card mb={0} {...rest}>
    <FlexWrapper>
      {icon}
      <LoadingBar ariaText="Loading plan information." />
    </FlexWrapper>
    <Body1 componentClass="h4" weight="bold" py={3} my={2} intuitProps={{ py: 2, my: 1 }}>
      {name}
    </Body1>
    <LoadingBar />
    <FlexWrapper pt={3} mt={2} intuitProps={{ pt: 2, mt: 1 }}>
      <LoadingBar />
      <LoadingBar />
    </FlexWrapper>
  </Card>
);

const MobileLoadingCard = ({ icon, name, ...rest }) => (
  <Card mb={0} {...rest}>
    <FlexWrapper>
      {icon}
      <Div ml={4} mr="auto">
        <LoadingBar />
        <Body1 componentClass="h4" weight="bold" my={2} intuitProps={{ my: 1 }}>
          {name}
        </Body1>
        <LoadingBar />
      </Div>
      <LoadingBar ml={4} />
    </FlexWrapper>
  </Card>
);

export const ResponsiveLoadingCard = (props) => <SizeSwitch xs={<MobileLoadingCard {...props} />} md={<LoadingCard {...props} />} />;

const SelectedCard = ({ children, icon, status, name, additionalInfo, ...rest }) => (
  <Card mb={0} {...rest}>
    <FlexWrapper>
      {icon}
      {status}
    </FlexWrapper>
    <Body1 componentClass="h4" weight="bold" pt={3} mt={2} mb={0} intuitProps={{ pt: 2, mt: 1 }}>
      {name}
    </Body1>
    <Caption color="muted" mt={1} pt={2}>
      {additionalInfo}
    </Caption>
    {children}
  </Card>
);

const MobileSelectedCard = ({ children, icon, status, name, additionalInfo, ...rest }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card mb={0} {...rest}>
      <FlexWrapper onClick={() => setOpen(!open)}>
        {icon}
        <Div ml={4} mr="auto">
          {status}
          <Body1 componentClass="h4" weight="bold" mt={2} mb={0} intuitProps={{ mt: 1 }}>
            {name}
          </Body1>
          {additionalInfo}
        </Div>
        <Button
          p={3}
          pr={0}
          color="tertiary"
          size="medium"
          intuitProps={{ color: "link", py: 0 }}
          classicProps={{ color: "link" }}
          aria-label={`Click to ${open ? "hide" : "show"} details`}
        >
          <Icon icon={open ? "chevron-up" : "chevron-down"} />
        </Button>
      </FlexWrapper>
      <Collapse in={open}>
        <div>
          {/* Collapse animation requires extra <div> to be smooth */}
          <div>{children}</div>
        </div>
      </Collapse>
    </Card>
  );
};

export const ResponsiveSelectedCard = (props) => <SizeSwitch xs={<MobileSelectedCard {...props} />} md={<SelectedCard {...props} />} />;

export const ErrorStatus = (props) => <Caption color="error" {...props} />;

export const WarningStatus = (props) => <Caption color="warning" {...props} />;

export const SuccessStatus = (props) => <Caption color="success" {...props} />;

export const StatusMessage = (props) => <Body2 color="muted" {...props} />;
